import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, RefreshCcw, ShieldCheck } from 'lucide-react';

import {
  approveEarlyAccessSubmission,
  getEarlyAccessApprovals,
  getEarlyAccessWaitlist,
  resendEarlyAccessApprovalEmail,
  resetEarlyAccessUnlock,
} from '../api/adminService';

const SECRET_STORAGE_KEY = 'forzaAdminSecret';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const StatusBadge = ({ status }) => {
  const className = status === 'consumed'
    ? 'border-emerald-300/20 bg-emerald-500/10 text-emerald-200'
    : status === 'unlock_active'
      ? 'border-amber-300/20 bg-amber-500/10 text-amber-200'
      : status === 'approved'
        ? 'border-cyan-300/20 bg-cyan-500/10 text-cyan-200'
        : 'border-slate-500/20 bg-slate-800 text-slate-300';

  return <span className={`rounded-full border px-2 py-1 text-xs font-semibold uppercase tracking-wide ${className}`}>{status}</span>;
};

const buildSubmissionDetails = (submission) => {
  if (!submission) {
    return [];
  }

  const optionalFields = [
    ['Name', submission.name],
    ['Goal', submission.goal],
    ['Device', submission.device],
    ['Country', submission.country],
    ['How did you hear about us', submission.heardAbout],
    ['Why do you want access', submission.whyAccess],
  ].filter(([, value]) => Boolean(value));

  return [
    ['Email', submission.email],
    ...optionalFields,
    ['Submitted', formatDateTime(submission.lastSubmittedAt)],
    ['Submit count', String(submission.submitCount || 1)],
  ];
};

const AdminEarlyAccessPage = () => {
  const [adminSecret, setAdminSecret] = useState(() => localStorage.getItem(SECRET_STORAGE_KEY) || '');
  const [submissions, setSubmissions] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [flashMessage, setFlashMessage] = useState('');

  const canLoad = adminSecret.trim().length > 0;

  const selectedSubmission = useMemo(
    () => submissions.find((item) => item._id === selectedSubmissionId) || submissions[0] || null,
    [selectedSubmissionId, submissions]
  );

  const loadData = useCallback(async () => {
    if (!canLoad) {
      setSubmissions([]);
      setApprovals([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      localStorage.setItem(SECRET_STORAGE_KEY, adminSecret);
      const [waitlistResponse, approvalsResponse] = await Promise.all([
        getEarlyAccessWaitlist({ adminSecret }),
        getEarlyAccessApprovals({ adminSecret }),
      ]);
      setSubmissions(waitlistResponse.data?.submissions || []);
      setApprovals(approvalsResponse.data?.approvals || []);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to load early access data.');
    } finally {
      setLoading(false);
    }
  }, [adminSecret, canLoad]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleApprove = async (submissionId) => {
    setBusyKey(`approve:${submissionId}`);
    setErrorMessage('');
    setFlashMessage('');
    try {
      const response = await approveEarlyAccessSubmission({ adminSecret, submissionId });
      if (response.data?.emailDelivery?.sent) {
        setFlashMessage('Early access approval saved and approval email sent.');
      } else if (response.data?.emailDelivery?.error) {
        setFlashMessage(`Early access approval saved, but email delivery failed: ${response.data.emailDelivery.error}`);
      } else {
        setFlashMessage('Early access approval saved.');
      }
      await loadData();
      setSelectedSubmissionId(submissionId);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to approve this submission.');
    } finally {
      setBusyKey('');
    }
  };

  const handleResendApprovalEmail = async (approvalId) => {
    setBusyKey(`resend:${approvalId}`);
    setErrorMessage('');
    setFlashMessage('');
    try {
      await resendEarlyAccessApprovalEmail({ adminSecret, approvalId });
      setFlashMessage('Approval email resent.');
      await loadData();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to resend the approval email.');
    } finally {
      setBusyKey('');
    }
  };

  const handleResetUnlock = async (approvalId) => {
    setBusyKey(`reset:${approvalId}`);
    setErrorMessage('');
    setFlashMessage('');
    try {
      await resetEarlyAccessUnlock({ adminSecret, approvalId });
      setFlashMessage('Unlock state reset.');
      await loadData();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to reset unlock state.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <main className="min-h-screen bg-[#070b11] px-5 py-10 text-white sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#84a2c8]">Admin</p>
            <h1 className="mt-3 text-4xl font-black tracking-tight text-white">Early Access Control</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-[#9bb1ce]">
              Review waitlist submissions, approve email-based access, and reset active unlock windows when needed.
            </p>
          </div>

          <div className="w-full max-w-md rounded-[1.4rem] border border-white/10 bg-[#0c1624]/70 p-4">
            <label className="block text-[11px] font-black uppercase tracking-[0.2em] text-[#84a2c8]">Admin secret</label>
            <input
              type="password"
              value={adminSecret}
              onChange={(event) => setAdminSecret(event.target.value)}
              className="mt-3 w-full rounded-xl border border-white/10 bg-[#07101a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-300/40 focus:ring-2 focus:ring-cyan-300/20"
              placeholder="Enter admin secret"
            />
          </div>
        </div>

        {errorMessage ? (
          <div className="mb-4 rounded-2xl border border-rose-300/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{errorMessage}</div>
        ) : null}
        {flashMessage ? (
          <div className="mb-4 rounded-2xl border border-emerald-300/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{flashMessage}</div>
        ) : null}

        {!canLoad ? (
          <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1624]/70 p-8 text-sm text-[#9bb1ce]">
            Enter the admin secret to load waitlist submissions and approvals.
          </div>
        ) : (
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.95fr)]">
            <section className="space-y-5">
              <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1624]/70 p-5 shadow-[0_24px_70px_rgba(2,8,23,0.36)]">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-black tracking-tight text-white">Waitlist submissions</h2>
                    <p className="mt-1 text-sm text-[#93abc8]">{loading ? 'Loading submissions...' : `${submissions.length} submissions loaded`}</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-[11px] uppercase tracking-[0.18em] text-[#7087a3]">
                      <tr>
                        <th className="pb-3 pr-4">Email</th>
                        <th className="pb-3 pr-4">Submitted</th>
                        <th className="pb-3 pr-4">Status</th>
                        <th className="pb-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((submission) => (
                        <tr
                          key={submission._id}
                          className={`border-t border-white/6 ${selectedSubmission?._id === submission._id ? 'bg-white/[0.03]' : ''}`}
                        >
                          <td className="py-3 pr-4 text-white">{submission.email}</td>
                          <td className="py-3 pr-4 text-[#c6d4e6]">{formatDateTime(submission.lastSubmittedAt)}</td>
                          <td className="py-3 pr-4"><StatusBadge status={submission.status || 'pending'} /></td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedSubmissionId(submission._id)}
                                className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-[#dce9f8] hover:bg-white/5"
                              >
                                View
                              </button>
                              {submission.status === 'pending' ? (
                                <button
                                  type="button"
                                  onClick={() => handleApprove(submission._id)}
                                  disabled={busyKey === `approve:${submission._id}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/20 disabled:opacity-50"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  Approve
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-[1.8rem] border border-white/10 bg-[#0c1624]/70 p-5 shadow-[0_24px_70px_rgba(2,8,23,0.36)]">
                <div className="mb-4 flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-cyan-200" />
                  <div>
                    <h2 className="text-xl font-black tracking-tight text-white">Approval status</h2>
                    <p className="mt-1 text-sm text-[#93abc8]">{loading ? 'Loading approvals...' : `${approvals.length} approvals loaded`}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {approvals.map((approval) => (
                    <article key={approval._id} className="rounded-[1.3rem] border border-white/8 bg-[#07101a] p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-sm font-bold text-white">{approval.approvedEmail}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <StatusBadge status={approval.status} />
                          </div>
                          <div className="mt-3 grid gap-1 text-xs text-[#9bb1ce]">
                            <p>Approved: <span className="text-[#e6effa]">{formatDateTime(approval.approvedAt)}</span></p>
                            <p>Approval email sent: <span className="text-[#e6effa]">{formatDateTime(approval.approvalEmailSentAt)}</span></p>
                            <p>Approval email sends: <span className="text-[#e6effa]">{approval.approvalEmailResendCount || 0}</span></p>
                            <p>Unlock issued: <span className="text-[#e6effa]">{formatDateTime(approval.unlockIssuedAt)}</span></p>
                            <p>Unlock count: <span className="text-[#e6effa]">{approval.unlockCount || 0}</span></p>
                            <p>Consumed: <span className="text-[#e6effa]">{formatDateTime(approval.consumedAt)}</span></p>
                            <p>Consumed by: <span className="text-[#e6effa]">{approval.consumedByUserId || 'N/A'}</span></p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => handleResendApprovalEmail(approval._id)}
                            disabled={busyKey === `resend:${approval._id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-4 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/20 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Resend approval email
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResetUnlock(approval._id)}
                            disabled={busyKey === `reset:${approval._id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-[#dce9f8] hover:bg-white/5 disabled:opacity-50"
                          >
                            <RefreshCcw className="h-3.5 w-3.5" />
                            Reset unlock
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <aside className="rounded-[1.8rem] border border-white/10 bg-[#0c1624]/70 p-5 shadow-[0_24px_70px_rgba(2,8,23,0.36)]">
              <h2 className="text-xl font-black tracking-tight text-white">Selected submission</h2>
              {!selectedSubmission ? (
                <p className="mt-4 text-sm text-[#93abc8]">Select a submission to inspect the full request.</p>
              ) : (
                <div className="mt-5 space-y-4">
                  {buildSubmissionDetails(selectedSubmission).map(([label, value]) => (
                    <div key={label} className="rounded-[1.2rem] border border-white/8 bg-[#07101a] p-4">
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#6f88a5]">{label}</p>
                      <p className="mt-2 text-sm leading-6 text-white">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminEarlyAccessPage;
